import type { ProductDto } from '@up2stay/up2promo-api-client';

export type ProductsListSortOption =
  | 'default'
  | 'nameAsc'
  | 'scansDesc'
  | 'locationAsc';

export const PRODUCTS_LIST_SORT_OPTIONS: ProductsListSortOption[] = [
  'default',
  'nameAsc',
  'scansDesc',
  'locationAsc',
];

function productSortName(product: ProductDto): string {
  return (product.label?.trim() || product.hash || '').trim();
}

export function sortProductsForList(
  products: ProductDto[],
  sort: ProductsListSortOption,
  locale: string,
  locationIdToName: Record<string, string>,
): ProductDto[] {
  if (sort === 'default') return products;

  const collator = new Intl.Collator(locale, {
    sensitivity: 'base',
    numeric: true,
  });

  return [...products].sort((a, b) => {
    switch (sort) {
      case 'nameAsc':
        return collator.compare(productSortName(a), productSortName(b));
      case 'scansDesc': {
        const aScans = a.scanCount ?? 0;
        const bScans = b.scanCount ?? 0;
        if (bScans !== aScans) return bScans - aScans;
        return collator.compare(productSortName(a), productSortName(b));
      }
      case 'locationAsc': {
        const locationName = (product: ProductDto) => {
          const id = product.target?.locationId;
          return id ? (locationIdToName[id] ?? id) : '';
        };
        const aLoc = locationName(a);
        const bLoc = locationName(b);
        // Unassigned last
        if (!aLoc && bLoc) return 1;
        if (aLoc && !bLoc) return -1;
        const byLoc = collator.compare(aLoc, bLoc);
        if (byLoc !== 0) return byLoc;
        return collator.compare(productSortName(a), productSortName(b));
      }
      default:
        return 0;
    }
  });
}
